import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ArrowRight, Loader2, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { PricingTier, BillingCycle } from './types';
import { cn } from '@/lib/core/utils';

interface PlanChangeConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPlan: PricingTier | null;
  newPlan: PricingTier;
  billingCycle: BillingCycle;
  onConfirm: () => Promise<void>;
}

export const PlanChangeConfirmModal: React.FC<PlanChangeConfirmModalProps> = ({
  open,
  onOpenChange,
  currentPlan,
  newPlan,
  billingCycle,
  onConfirm,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const currentPrice = currentPlan?.monthlyPrice !== null && currentPlan?.monthlyPrice !== undefined
    ? billingCycle === 'yearly'
      ? Math.round(currentPlan.yearlyPrice! / 12)
      : currentPlan.monthlyPrice
    : 0;

  const newPrice = newPlan.monthlyPrice !== null
    ? billingCycle === 'yearly'
      ? Math.round(newPlan.yearlyPrice! / 12)
      : newPlan.monthlyPrice
    : null;

  const isUpgrade = newPrice !== null && newPrice > currentPrice;
  const isDowngrade = newPrice !== null && newPrice < currentPrice;
  const isSameTier = currentPlan?.id === newPlan.id;
  const isEnterprise = newPlan.monthlyPrice === null;

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in parent
    } finally {
      setIsLoading(false);
    }
  };

  const getChangeIcon = () => {
    if (isEnterprise) return <ArrowRight className="h-5 w-5 text-muted-foreground" />;
    if (isUpgrade) return <TrendingUp className="h-5 w-5 text-green-500" />;
    if (isDowngrade) return <TrendingDown className="h-5 w-5 text-orange-500" />;
    return <Minus className="h-5 w-5 text-muted-foreground" />;
  };

  const getChangeText = () => {
    if (isEnterprise) return 'Custom pricing - our team will contact you';
    if (isUpgrade) return 'Upgrade - prorated charge applied immediately';
    if (isDowngrade) return 'Downgrade - credit applied to next billing';
    return 'Billing cycle change';
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm Plan Change</AlertDialogTitle>
          <AlertDialogDescription>
            {isEnterprise
              ? 'You\'re requesting an Enterprise plan. Our team will contact you to discuss your needs.'
              : 'Review your plan change below.'}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          {/* Plan Comparison */}
          <div className="flex items-center justify-between gap-4">
            {/* Current Plan */}
            <div className="flex-1 text-center">
              <p className="text-xs text-muted-foreground mb-1">Current</p>
              <div className="p-3 rounded-lg bg-muted/50 border">
                <p className="font-semibold">{currentPlan?.name || 'No Plan'}</p>
                {currentPlan && currentPrice > 0 && (
                  <p className="text-sm text-muted-foreground">€{currentPrice}/mo</p>
                )}
              </div>
            </div>

            {/* Arrow */}
            <div className="flex-shrink-0">
              {getChangeIcon()}
            </div>

            {/* New Plan */}
            <div className="flex-1 text-center">
              <p className="text-xs text-muted-foreground mb-1">New</p>
              <div className={cn(
                'p-3 rounded-lg border',
                isUpgrade ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' :
                isDowngrade ? 'bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800' :
                'bg-primary/5 border-primary/20'
              )}>
                <p className="font-semibold">{newPlan.name}</p>
                {newPrice !== null ? (
                  <p className="text-sm text-muted-foreground">€{newPrice}/mo</p>
                ) : (
                  <p className="text-sm text-muted-foreground">Custom</p>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* Change Details */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              {getChangeIcon()}
              <span className="text-muted-foreground">{getChangeText()}</span>
            </div>

            {billingCycle === 'yearly' && !isEnterprise && (
              <Badge className="text-xs">
                Billed yearly ({newPrice !== null ? `€${newPlan.yearlyPrice}` : 'Custom'}/year)
              </Badge>
            )}

            {isUpgrade && !isEnterprise && (
              <p className="text-xs text-muted-foreground">
                You'll be charged the difference for the remaining days in your billing period.
              </p>
            )}

            {isDowngrade && (
              <p className="text-xs text-muted-foreground">
                Your new plan will take effect at the end of your current billing period.
                Any unused credit will be applied.
              </p>
            )}
          </div>

          {/* New Plan Features */}
          {!isSameTier && (
            <>
              <Separator />
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  {newPlan.name} includes:
                </p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  {newPlan.features.slice(0, 4).map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-1">
                      <span className="text-green-500">✓</span> {feature}
                    </li>
                  ))}
                  {newPlan.features.length > 4 && (
                    <li className="text-muted-foreground/70">
                      +{newPlan.features.length - 4} more features
                    </li>
                  )}
                </ul>
              </div>
            </>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className={cn(
              isUpgrade ? 'bg-green-600 hover:bg-green-700' :
              isDowngrade ? 'bg-orange-600 hover:bg-orange-700' :
              ''
            )}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : isEnterprise ? (
              'Contact Sales'
            ) : isUpgrade ? (
              'Confirm Upgrade'
            ) : isDowngrade ? (
              'Confirm Downgrade'
            ) : (
              'Confirm Change'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default PlanChangeConfirmModal;
